import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import api from "../../../lib/api/api";
import AdminNavbar from "./AdminNavbar";

const emptyForm = {
  titleEn: "",
  titleDe: "",
  titleAr: "",
  descriptionEn: "",
  descriptionDe: "",
  descriptionAr: "",
  eventDateTime: "",
  zoomLink: "",
  assignedTeacherId: "",
  displayOrder: "",
  published: true,
  status: "DRAFT",
};

const eventStatuses = [
  { value: "DRAFT", label: "Draft", meaning: "Admin only. Use while the event information is still being prepared." },
  { value: "COMING_SOON", label: "Coming soon", meaning: "Public announcement. Date, Zoom link and assigned person may all be added later." },
  { value: "SCHEDULED", label: "Scheduled", meaning: "The date is confirmed. The presenter and Zoom link may still be added later." },
  { value: "LIVE", label: "Live", meaning: "The event can be joined now. A date and Zoom link are required and the public Join button is enabled." },
  { value: "FINISHED", label: "Finished", meaning: "The event has ended. A date is required and joining is disabled." },
  { value: "CANCELLED", label: "Cancelled", meaning: "The event was cancelled. It may remain published so visitors can see the cancellation." },
];

const toInputDateTime = (value) => (value ? value.slice(0, 16) : "");
const toApiPayload = (form) => ({
  ...form,
  eventDateTime: form.eventDateTime ? `${form.eventDateTime}:00` : null,
  assignedTeacherId: form.assignedTeacherId ? Number(form.assignedTeacherId) : null,
  displayOrder: form.displayOrder === "" ? null : Number(form.displayOrder),
});

const EventAdminPage = () => {
  const [events, setEvents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    const [eventsResponse, teachersResponse] = await Promise.all([
      api.get("/admin/events"),
      api.get("/admin/teacherDetails"),
    ]);
    setEvents(Array.isArray(eventsResponse.data) ? eventsResponse.data : []);
    setTeachers(Array.isArray(teachersResponse.data) ? teachersResponse.data : []);
  };

  useEffect(() => {
    loadData().catch(() => setError("Failed to load events."));
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    setForm({
      titleEn: event.titleEn || "",
      titleDe: event.titleDe || "",
      titleAr: event.titleAr || "",
      descriptionEn: event.descriptionEn || "",
      descriptionDe: event.descriptionDe || "",
      descriptionAr: event.descriptionAr || "",
      eventDateTime: toInputDateTime(event.eventDateTime),
      zoomLink: event.zoomLink || "",
      assignedTeacherId: event.assignedTeacherId || "",
      displayOrder: event.displayOrder ?? "",
      published: Boolean(event.published),
      status: event.status || "COMING_SOON",
    });
    setEditingId(event.id);
    setError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!saving) setDialogOpen(false);
  };

  const handleChange = (field) => (event) => {
    const value = field === "published" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveEvent = async () => {
    if (!form.titleEn.trim() && !form.titleDe.trim() && !form.titleAr.trim()) {
      setError("Add at least one event title.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = toApiPayload(form);
      if (editingId) {
        await api.put(`/admin/events/${editingId}`, payload);
      } else {
        await api.post("/admin/events", payload);
      }
      await loadData();
      setDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save event.");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (eventId) => {
    const confirmed = window.confirm("Delete this event?");
    if (!confirmed) return;

    try {
      await api.delete(`/admin/events/${eventId}`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete event.");
    }
  };

  const teacherName = (event) =>
    [event.assignedTeacherFirstName, event.assignedTeacherLastName].filter(Boolean).join(" ") || "Not assigned";

  return (
    <Box>
      <AdminNavbar />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={800}>
                Events
              </Typography>
              <Typography color="text.secondary">
                Create public events and assign each one to a team member.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Add Event
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Assigned person</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>{event.titleEn || event.titleDe || event.titleAr || "Untitled"}</TableCell>
                  <TableCell>{event.eventDateTime ? new Date(event.eventDateTime).toLocaleString() : "No date"}</TableCell>
                  <TableCell>{teacherName(event)}</TableCell>
                  <TableCell>{event.published ? "Yes" : "No"}</TableCell>
                  <TableCell>{eventStatuses.find((item) => item.value === event.status)?.label || event.status || "Coming soon"}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEdit(event)} aria-label="Edit event">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => deleteEvent(event.id)} aria-label="Delete event" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No events yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? "Edit Event" : "Add Event"}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography fontWeight={800} sx={{ mb: 1 }}>What every event status means</Typography>
            {eventStatuses.map((item) => (
              <Typography variant="body2" key={item.value} sx={{ mb: 0.5 }}>
                <strong>{item.label}:</strong> {item.meaning}
              </Typography>
            ))}
          </Alert>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Event status</InputLabel>
                <Select label="Event status" value={form.status} onChange={handleChange("status")}>
                  {eventStatuses.map((item) => (
                    <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                  {eventStatuses.find((item) => item.value === form.status)?.meaning}
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Alert severity={form.status === "DRAFT" ? "warning" : "success"}>
                {form.status === "DRAFT"
                  ? "Draft events are always hidden from the public, even when Published is checked."
                  : "Published controls whether this event appears on the public Events page."}
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Title EN" value={form.titleEn} onChange={handleChange("titleEn")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Title DE" value={form.titleDe} onChange={handleChange("titleDe")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Title AR" value={form.titleAr} onChange={handleChange("titleAr")} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Description EN"
                value={form.descriptionEn}
                onChange={handleChange("descriptionEn")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Description DE"
                value={form.descriptionDe}
                onChange={handleChange("descriptionDe")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Description AR"
                value={form.descriptionAr}
                onChange={handleChange("descriptionAr")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Event date and time"
                value={form.eventDateTime}
                onChange={handleChange("eventDateTime")}
                InputLabelProps={{ shrink: true }}
                required={["SCHEDULED", "LIVE", "FINISHED"].includes(form.status)}
                helperText={form.status === "COMING_SOON" ? "Optional — visitors will see that the date is coming soon." : ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Zoom link"
                value={form.zoomLink}
                onChange={handleChange("zoomLink")}
                required={form.status === "LIVE"}
                helperText={form.status === "LIVE" ? "Required for Live; enables the public Join button." : "Optional — online access can be announced later."}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Display order"
                value={form.displayOrder}
                onChange={handleChange("displayOrder")}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <InputLabel>Assigned team member</InputLabel>
                <Select
                  label="Assigned team member"
                  value={form.assignedTeacherId}
                  onChange={handleChange("assignedTeacherId")}
                >
                  <MenuItem value="">Not assigned</MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.teacherId} value={teacher.teacherId}>
                      {[teacher.firstName, teacher.lastName].filter(Boolean).join(" ") || `Teacher ${teacher.teacherId}`}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                  Optional — visitors will see that the presenter will be announced soon.
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Checkbox checked={form.published} onChange={handleChange("published")} />}
                label="Published"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={saveEvent} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventAdminPage;
